import { useEffect } from 'react';
import { liveQuery } from 'dexie';
import { db, seedDatabase } from '../db/database';
import useDataStore from '../store/useDataStore';
import useAppStore from '../store/useAppStore';
import useAuthStore from '../store/useAuthStore';
import { triggerSync } from '../services/data';

// Mounts ONCE at App root. Seeds Dexie, hydrates the data store from each table,
// then runs liveQuery subscriptions so any Dexie write re-pushes the changed
// table into Zustand exactly once per change.
export default function useDexieSync() {
  const setInitialized = useDataStore((s) => s.setInitialized);
  const setUnitSystem = useAppStore((s) => s.setUnitSystem);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isConfigured = useAuthStore((s) => s.isConfigured);
  const initialized = useDataStore((s) => s.initialized);

  useEffect(() => {
    let subs = [];
    let cancelled = false;

    seedDatabase().then(() => {
      if (cancelled) return;
      setInitialized(true);

      db.settings.get('unitSystem').then((setting) => {
        if (setting) setUnitSystem(setting.value);
      });

      const tables = [
        ['recipes', 'setRecipes', () => db.recipes.toArray()],
        ['categories', 'setCategories', () => db.categories.orderBy('position').toArray()],
        ['groceryLists', 'setGroceryLists', () => db.groceryLists.toArray()],
        ['groceryItems', 'setGroceryItems', () => db.groceryItems.toArray()],
        ['mealPlans', 'setMealPlans', () => db.mealPlans.toArray()],
      ];
      subs = tables.map(([_name, setter, fn]) =>
        liveQuery(fn).subscribe({
          next: (data) => useDataStore.getState()[setter](data),
          error: (err) => console.warn(`liveQuery error: ${err.message}`),
        })
      );
    });

    return () => {
      cancelled = true;
      subs.forEach((s) => s.unsubscribe?.());
    };
  }, [setInitialized, setUnitSystem]);

  // Pull from cloud once, when auth becomes ready and Dexie is hydrated.
  useEffect(() => {
    if (isAuthenticated && isConfigured && initialized) {
      triggerSync().catch((err) => console.warn('Sync failed:', err));
    }
  }, [isAuthenticated, isConfigured, initialized]);
}
